import tenantContentService from '../../services/tenantContentService';

export default {
  namespaced: true,

  state: {
    knowledgeArticles: [],
    userDemoContent: [],
    externalEvaluationText: { en: '', de: '' },
    tagGroups: [],
    features: [],
    loading: false,
    error: null,
  },

  mutations: {
    SET_KNOWLEDGE_ARTICLES(state, articles) {
      state.knowledgeArticles = articles;
    },
    SET_USER_DEMO_CONTENT(state, content) {
      state.userDemoContent = content;
    },
    SET_EXTERNAL_EVALUATION_TEXT(state, content) {
      state.externalEvaluationText = content || { en: '', de: '' };
    },
    SET_TAG_GROUPS(state, tagGroups) {
      state.tagGroups = tagGroups;
    },
    SET_FEATURES(state, features) {
      state.features = features;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
  },

  actions: {
    // ============= Knowledge Database =============

    async fetchKnowledgeArticles({ commit, dispatch }) {
      commit('SET_LOADING', true);
      try {
        const response = await tenantContentService.getKnowledgeDatabase();
        commit('SET_KNOWLEDGE_ARTICLES', response.data.content || []);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch articles');
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to fetch knowledge articles', severity: 'error' },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createKnowledgeArticle({ dispatch }, article) {
      try {
        await tenantContentService.createKnowledgeArticle(article);
        dispatch('fetchKnowledgeArticles');
        dispatch(
          'notifications/showToast',
          { detail: 'Article created successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to create article', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    async updateKnowledgeArticle({ dispatch }, { index, article }) {
      try {
        await tenantContentService.updateKnowledgeArticle(index, article);
        dispatch('fetchKnowledgeArticles');
        dispatch(
          'notifications/showToast',
          { detail: 'Article updated successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to update article', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    async deleteKnowledgeArticle({ dispatch }, index) {
      try {
        await tenantContentService.deleteKnowledgeArticle(index);
        dispatch('fetchKnowledgeArticles');
        dispatch(
          'notifications/showToast',
          { detail: 'Article deleted successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to delete article', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    async reorderKnowledgeArticles({ commit, dispatch }, articles) {
      commit('SET_KNOWLEDGE_ARTICLES', articles);
      try {
        await tenantContentService.reorderKnowledgeArticles(articles);
      } catch (error) {
        dispatch('fetchKnowledgeArticles');
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to save order', severity: 'error' },
          { root: true },
        );
      }
    },

    // ============= User Demo Content =============

    async fetchUserDemoContent({ commit, dispatch }) {
      commit('SET_LOADING', true);
      try {
        const response = await tenantContentService.getUserDemoContent();
        commit('SET_USER_DEMO_CONTENT', response.data.content || []);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch demo content');
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to fetch demo content', severity: 'error' },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createUserDemoContent({ dispatch }, content) {
      try {
        await tenantContentService.createUserDemoContent(content);
        dispatch('fetchUserDemoContent');
        dispatch(
          'notifications/showToast',
          { detail: 'Demo content created successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to create demo content', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    async updateUserDemoContent({ dispatch }, { index, content }) {
      try {
        await tenantContentService.updateUserDemoContent(index, content);
        dispatch('fetchUserDemoContent');
        dispatch(
          'notifications/showToast',
          { detail: 'Demo content updated successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to update demo content', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    async deleteUserDemoContent({ dispatch }, index) {
      try {
        await tenantContentService.deleteUserDemoContent(index);
        dispatch('fetchUserDemoContent');
        dispatch(
          'notifications/showToast',
          { detail: 'Demo content deleted successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to delete demo content', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    async reorderUserDemoContent({ commit, dispatch }, content) {
      commit('SET_USER_DEMO_CONTENT', content);
      try {
        await tenantContentService.reorderUserDemoContent(content);
      } catch (error) {
        dispatch('fetchUserDemoContent');
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to save order', severity: 'error' },
          { root: true },
        );
      }
    },

    // ============= External Evaluation Text =============

    async fetchExternalEvaluationText({ commit, dispatch }) {
      commit('SET_LOADING', true);
      try {
        const response = await tenantContentService.getExternalEvaluationText();
        commit('SET_EXTERNAL_EVALUATION_TEXT', response.data.content || { en: '', de: '' });
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_EXTERNAL_EVALUATION_TEXT', { en: '', de: '' });
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch external evaluation text');
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to fetch external evaluation text', severity: 'error' },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateExternalEvaluationText({ commit, dispatch }, content) {
      try {
        const response = await tenantContentService.updateExternalEvaluationText(content);
        commit('SET_EXTERNAL_EVALUATION_TEXT', response.data.content || { en: '', de: '' });
        dispatch(
          'notifications/showToast',
          { detail: 'External evaluation text saved successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to save external evaluation text', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    // ============= Tag Groups =============

    async fetchTagGroups({ commit, dispatch }) {
      commit('SET_LOADING', true);
      try {
        const response = await tenantContentService.getTagGroups();
        commit('SET_TAG_GROUPS', response.data.data || []);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch tag groups');
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to fetch tag groups', severity: 'error' },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createTagGroup({ dispatch }, tagGroup) {
      try {
        await tenantContentService.createTagGroup(tagGroup);
        dispatch('fetchTagGroups');
        dispatch(
          'notifications/showToast',
          { detail: 'Tag group created successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to create tag group', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    async updateTagGroup({ dispatch }, { index, tagGroup }) {
      try {
        await tenantContentService.updateTagGroup(index, tagGroup);
        dispatch('fetchTagGroups');
        dispatch(
          'notifications/showToast',
          { detail: 'Tag group updated successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to update tag group', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    async deleteTagGroup({ dispatch }, index) {
      try {
        await tenantContentService.deleteTagGroup(index);
        dispatch('fetchTagGroups');
        dispatch(
          'notifications/showToast',
          { detail: 'Tag group deleted successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to delete tag group', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    async reorderTagGroups({ commit, dispatch }, tagGroups) {
      commit('SET_TAG_GROUPS', tagGroups);
      try {
        await tenantContentService.reorderTagGroups(tagGroups);
      } catch (error) {
        dispatch('fetchTagGroups');
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to save order', severity: 'error' },
          { root: true },
        );
      }
    },

    // ============= Features =============

    async fetchFeatures({ commit, dispatch }) {
      commit('SET_LOADING', true);
      try {
        const response = await tenantContentService.getFeatures();
        commit('SET_FEATURES', response.data.data || []);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch features');
        dispatch(
          'notifications/showToast',
          { detail: 'Failed to fetch features', severity: 'error' },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createFeature({ dispatch }, feature) {
      try {
        await tenantContentService.createFeature(feature);
        dispatch('fetchFeatures');
        dispatch(
          'notifications/showToast',
          { detail: 'Feature created successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: error.response?.data?.message || 'Failed to create feature', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    async updateFeature({ dispatch }, { id, feature }) {
      try {
        await tenantContentService.updateFeature(id, feature);
        dispatch('fetchFeatures');
        dispatch(
          'notifications/showToast',
          { detail: 'Feature updated successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch('fetchFeatures');
        dispatch(
          'notifications/showToast',
          { detail: error.response?.data?.message || 'Failed to update feature', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },

    async deleteFeature({ dispatch }, id) {
      try {
        await tenantContentService.deleteFeature(id);
        dispatch('fetchFeatures');
        dispatch(
          'notifications/showToast',
          { detail: 'Feature deleted successfully', severity: 'success' },
          { root: true },
        );
        return true;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          { detail: error.response?.data?.message || 'Failed to delete feature', severity: 'error' },
          { root: true },
        );
        return false;
      }
    },
  },

  getters: {
    knowledgeArticles: (state) => state.knowledgeArticles,
    userDemoContent: (state) => state.userDemoContent,
    externalEvaluationText: (state) => state.externalEvaluationText,
    tagGroups: (state) => state.tagGroups,
    features: (state) => state.features,
    loading: (state) => state.loading,
    error: (state) => state.error,
  },
};
